import { create } from "zustand";

import type { AppointmentState } from "./appointment-store.types";

import {
  BOOKER_CONTACT_DATA,
  CONTACT_FORM_INITIAL_VALUES,
  initialAppointmentData,
  initialTimeIntervals,
  userAppointmentData,
} from "./appointment-store.constants";

export const useAppointmentStore = create<AppointmentState>(set => ({
  // Initial state
  currentEventTypeId: -1,
  isCalendarLoaded: false,
  timeIntervals: initialTimeIntervals,
  dateToDisplay: new Date(),
  // appointment data for setting the appointment, i.e: date, time
  appointmentData: initialAppointmentData,
  // appointment data that is coming from the API
  userAppointmentData,
  contactDetails: BOOKER_CONTACT_DATA,
  initialContactFormData: CONTACT_FORM_INITIAL_VALUES,
  otherParticipants: [],
  currentSelectedParticipant: [],
  customFields: {},
  // used for mobile mainly for now
  customFieldsKeys: [],
  isFetchingIntervals: false,

  // Actions
  setTimeRange: payload =>
    set(state => ({
      appointmentData: {
        ...state.appointmentData,
        timeRange: payload,
      },
    })),

  resetTimeRange: () =>
    set(state => ({
      appointmentData: {
        ...state.appointmentData,
        timeRange: initialAppointmentData.timeRange,
        date: initialAppointmentData.date,
      },
    })),

  setDate: payload =>
    set(state => ({
      appointmentData: {
        ...state.appointmentData,
        date: payload,
      },
    })),

  setUserAppointmentData: payload =>
    set(() => ({
      userAppointmentData: payload,
    })),

  resetDate: () =>
    set(state => ({
      appointmentData: {
        ...state.appointmentData,
        date: initialAppointmentData.date,
      },
    })),

  setGlobalDateToDisplay: payload =>
    set(() => ({
      dateToDisplay: payload,
    })),

  setContactDetails: payload =>
    set(() => ({
      contactDetails: payload,
    })),

  addParticipant: payload =>
    set(state => ({
      otherParticipants: [...state.otherParticipants, { ...payload }],
    })),

  setCurrentSelectedParticipant: payload =>
    set(state => ({
      currentSelectedParticipant: [...state.currentSelectedParticipant, { ...payload }],
    })),

  resetCurrentSelectedParticipant: () =>
    set(() => ({
      currentSelectedParticipant: [],
    })),

  updateParticipant: (id, payload) =>
    set(state => ({
      otherParticipants: state.otherParticipants.map(person => (person.id === id ? { ...person, ...payload } : person)),
    })),

  deleteParticipant: payload =>
    set(state => ({
      otherParticipants: state.otherParticipants.filter(person => person.id !== payload),
    })),

  resetOtherParticipants: () =>
    set(() => ({
      otherParticipants: [],
    })),

  setCurrentEventTypeId: payload =>
    set(() => ({
      currentEventTypeId: payload,
    })),

  setTimeIntervals: payload =>
    set(() => ({
      timeIntervals: payload,
    })),

  setIsCalendarLoaded: payload =>
    set(() => ({
      isCalendarLoaded: payload,
    })),

  setCustomFields: payload =>
    set(() => ({
      customFields: payload,
    })),
  setCustomFieldKeys: payload =>
    set(() => ({
      customFieldsKeys: payload,
    })),

  setContactFormData: payload =>
    set(() => ({
      initialContactFormData: payload,
    })),

  setIsFetchingIntervals: payload =>
    set(() => ({
      isFetchingIntervals: payload,
    })),

  removeTimeSlot: (date: string, timeRange: Array<string>) =>
    set((state) => {
      const dayIntervals = state.timeIntervals[date];
      if (!dayIntervals)
        return state;

      const [fromTime, toTime] = timeRange.map(time => time.trim());
      const updatedIntervals = dayIntervals.intervals.filter(
        interval => !(interval.from.trim() === fromTime && interval.to.trim() === toTime),
      );

      return {
        ...state,
        timeIntervals: {
          ...state.timeIntervals,
          [date]: {
            ...dayIntervals,
            intervals: updatedIntervals,
          },
        },
      };
    }),
}));
